import React, { useState } from "react";
import { IconButton, makeStyles } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { InputBaseComponentProps } from "@material-ui/core/InputBase/InputBase";
import { StandardTextFieldProps, TextFieldProps } from "@material-ui/core/TextField/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import ErrorIcon from "@material-ui/icons/Error";
import ClearIcon from "@material-ui/icons/Clear";
import clsx from "clsx";
import { tesaGrey1, tesaDarkRedError, tesaBlue, tesaGrey2 } from "../../styles/colors";
import MaskedInput from "./MaskedInput";
import { MaskConfiguration } from "./MaskConfigurations";

export interface CustomTextFieldProps extends StandardTextFieldProps {
  characterLimit?: number;
  clamped?: boolean;
  helperText?: React.ReactNode | string;
  helperTextOnlyOnFocus?: boolean;
  icon?: boolean;
  onClear?: () => void;
  mandatory?: boolean;
  masked?: MaskConfiguration;
  noSpinners?: boolean;
  stretch?: boolean;
}

const useStyles = makeStyles(() => ({
  root: (props: CustomTextFieldProps) => ({
    "& .MuiOutlinedInput-inputMultiline": {
      marginTop: props.label ? 10 : 0,
    },
    "& input[type=number]": props.noSpinners
      ? {
          "-moz-appearance": "textfield",
          "&::-webkit-inner-spin-button, ::-webkit-outer-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
          },
        }
      : undefined,
  }),
  clearIcon: {
    fontSize: "1rem",
    cursor: "pointer",
  },
  clearIconContainer: {
    borderRadius: "50%",
    color: tesaGrey1,
    fontSize: "1rem",
    padding: "5px",
    "&:hover": {
      color: "white",
      backgroundColor: tesaGrey2,
    },
  },
  icon: {
    color: tesaGrey1,
    lineHeight: "1.5rem",
    position: "absolute",
    right: 12,
    top: 14,
  },
  iconDefault: {
    color: tesaGrey1,
  },
  iconError: {
    color: tesaDarkRedError,
  },
  iconBlue: {
    color: tesaBlue,
  },
  iconRaised: {
    top: 14,
  },
  stretch: {
    flexGrow: 1,
  },
}));

const CustomTextField: React.FC<MergedProps<CustomTextFieldProps, TextFieldProps> & { endIcon?: JSX.Element }> = (
  props,
) => {
  const [isFocused, setIsFocused] = useState(false);

  const classes = useStyles(props);
  const {
    characterLimit,
    clamped,
    className,
    onClear,
    disabled,
    helperTextOnlyOnFocus,
    endIcon,
    error,
    fullWidth,
    helperText,
    icon,
    id,
    label,
    mandatory,
    masked,
    multiline,
    name,
    onBlur,
    onChange,
    onFocus,
    ref,
    rows,
    stretch,
    type,
    value,
    InputProps,
    ...TextFieldProps
  } = props;

  const setFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (isFocused) {
      setIsFocused(false);
    } else {
      setIsFocused(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const onBlurLocal: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e): void => {
    setFocus(e);
    onBlur?.(e);
  };

  const renderHelperText = (): React.ReactNode | string => {
    if (helperTextOnlyOnFocus) {
      return isFocused ? helperText : "";
    }
    return helperText;
  };

  return (
    <TextField
      ref={ref}
      className={[classes.root, className, stretch && classes.stretch].join(" ")}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      helperText={renderHelperText()}
      id={id}
      InputProps={{
        endAdornment: endIcon ? (
          <InputAdornment position="end" className={isFocused ? classes.iconBlue : classes.iconDefault}>
            {endIcon}
          </InputAdornment>
        ) : onClear && value ? (
          <IconButton className={classes.clearIconContainer} onClick={onClear}>
            <ClearIcon className={classes.clearIcon} />
          </IconButton>
        ) : (
          icon && (
            <InputAdornment className="muiErrorIcon" position="start">
              <ErrorIcon
                className={[classes.icon, label && classes.iconRaised, error && classes.iconError].join(" ")}
              />
              {InputProps?.startAdornment}
            </InputAdornment>
          )
        ),
        onFocus: setFocus,
        onBlur: onBlurLocal,
        inputProps: {
          maxLength: characterLimit || (multiline ? 6000 : 100),
          masked,
        },
        inputComponent: masked && (MaskedInput as React.ElementType<InputBaseComponentProps>),
      }}
      InputLabelProps={
        masked && value
          ? {
              shrink: true,
            }
          : undefined
      }
      FormHelperTextProps={{
        classes: { root: clsx({ clamped }) },
      }}
      label={mandatory ? `${label}*` : label}
      multiline={multiline}
      name={name}
      onChange={(e): void => {
        if (onChange) {
          onChange(e);
        }
      }}
      rows={rows}
      type={type}
      value={value}
      variant="outlined"
      {...TextFieldProps}
    />
  );
};

export default CustomTextField;
